// src/presentation/hooks/campaign/useInventoryLists.ts
import { useCallback, useState } from 'react'
import { useAuth0 } from '@auth0/auth0-react'

import { GraphQLInventoryRepository } from '../../../infrastructure/graphql/InventoryRepositoryImpl'
import { GraphQLTagRepository } from '../../../infrastructure/graphql/TagRepositoryImpl'
import { fetchInventories } from '../../../application/usecases/fetchInventories'
import { fetchTagsByCampaignId } from '../../../application/usecases/fetchTagsByCampaignId'
import { Inventory } from '../../../domain/entities/Inventory'
import { Tag } from '../../../domain/entities/Tag'

async function getAllInventories(
  getToken: () => Promise<string>
): Promise<Inventory[]> {
  const repo = new GraphQLInventoryRepository(getToken)
  const exec = fetchInventories(repo)
  return exec()
}

async function getTagsForCampaign(
  getToken: () => Promise<string>,
  campaignId: string
): Promise<Tag[]> {
  const repo = new GraphQLTagRepository(getToken)
  const exec = await fetchTagsByCampaignId(repo)
  return exec(campaignId)
}

async function loadInventoryLists(
  getToken: () => Promise<string>,
  campaignId: string | undefined
): Promise<{
  connected: Inventory[]
  available: Inventory[]
}> {
  const [inventories, tags] = await Promise.all([
    getAllInventories(getToken),
    campaignId
      ? getTagsForCampaign(getToken, campaignId)
      : Promise.resolve<Tag[]>([]),
  ])
  const linkedIds = new Set(tags.map((t) => t.inventoryID))

  const connected = inventories.filter((inv) => linkedIds.has(inv.id))
  const available = inventories.filter((inv) => !linkedIds.has(inv.id))

  return { connected, available }
}

export function useInventoryListsForCampaign() {
  const { getIdTokenClaims } = useAuth0()
  const [isLoading, setIsLoading] = useState(false)

  const loadInventoryListsCallback = useCallback(
    async (campaignId: string | undefined) => {
      setIsLoading(true)
      const getToken = async () => (await getIdTokenClaims())?.__raw ?? ''
      const inventoryList = await loadInventoryLists(getToken, campaignId)
      setIsLoading(false)
      return inventoryList
    },
    [getIdTokenClaims]
  )
  return { loadInventoryListsCallback, isLoading }
}
